import {
  CSSProperties,
  FunctionComponent,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useOnClickOutside } from '../../hooks';
import { isKeyboardFocus } from '../../utils';
import { IconLabel, IconLabelSizes } from '../IconLabel';

import classNames from 'classnames';
import classes from './Popup.module.scss';

interface PopupProps {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  body: JSX.Element;
  bodyTop?: string;
  bodyRight?: string;
  bodyBottom?: string;
  bodyLeft?: string;
  bodyWidth?: string;
  bodyPosition?: CSSProperties['position'];
  bodyClassName?: string;
  iconId?: string;
  iconUrl?: string;
  iconSize?: number;
  label?: string;
  ariaLabel?: string;
  labelFirst?: boolean;
  labelSize?: IconLabelSizes;
  color?: string;
  hoverColor?: string;
  singleColor?: boolean;
  disabled?: boolean;
  parentClassName?: string;
  className?: string;
  labelClassName?: string;
  onClick?: () => void;
  onClose?: () => void;
}

export const Popup: FunctionComponent<PopupProps> = memo(
  ({
    isOpen,
    setIsOpen,
    body,
    bodyTop,
    bodyRight,
    bodyBottom,
    bodyLeft,
    bodyWidth,
    bodyPosition,
    bodyClassName,
    iconId,
    iconUrl,
    iconSize,
    label,
    ariaLabel,
    labelFirst,
    labelSize,
    color,
    hoverColor,
    singleColor,
    disabled,
    parentClassName,
    className,
    labelClassName,
    onClick,
    onClose,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const closeHandler = useCallback(() => setIsOpen?.(false), [setIsOpen]);

    useOnClickOutside(containerRef, closeHandler);

    const onClickHandler = useCallback(() => {
      setIsOpen?.(!isOpen);
      onClick?.();
    }, [isOpen, onClick, setIsOpen]);

    const keyDownHandler = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Escape' || event.defaultPrevented) {
          return;
        }

        event.stopPropagation();
        closeHandler();
      },
      [closeHandler]
    );

    useEffect(() => {
      if (!isOpen) {
        onClose?.();
      }
    }, [isOpen, onClose]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      const opener = document.activeElement;

      const isOpenedByKeyboard = isKeyboardFocus();

      return () => {
        if (
          isOpenedByKeyboard &&
          document.activeElement === document.body &&
          opener instanceof HTMLElement
        ) {
          opener.focus({ preventScroll: true });
        }
      };
    }, [isOpen]);

    return (
      <div
        ref={containerRef}
        className={classNames(classes['popup'], parentClassName)}
        {...(isOpen && setIsOpen && { onKeyDown: keyDownHandler })}
      >
        <IconLabel
          iconId={iconId}
          iconUrl={iconUrl}
          iconSize={iconSize}
          label={label}
          ariaLabel={ariaLabel}
          labelFirst={labelFirst}
          labelSize={labelSize}
          color={color}
          hoverColor={hoverColor}
          disabled={disabled}
          singleColor={singleColor}
          className={className}
          labelClassName={labelClassName}
          onClick={onClickHandler}
        />

        {isOpen && (
          <div
            className={classNames(classes['popup__content'], bodyClassName)}
            style={{
              top: bodyTop,
              right: bodyRight,
              bottom: bodyBottom,
              left: bodyLeft,
              width: bodyWidth,
              position: bodyPosition,
            }}
          >
            {body}
          </div>
        )}
      </div>
    );
  }
);
